import { useParams } from 'next/navigation';
import { useTranslations } from 'use-intl';
import Link from '@/components/atoms/link';
import { Locale } from '@/project.config';
import { buildCategoryCrumbs } from '@/utils/build-category-crumbs';
import { PDPHeaderProps } from '../types';

// EWM-192 — Dan's 3-level breadcrumb: `{L1 Category} / {L2 Category} /
// {Product Name}`. The product name is the current-page leaf — rendered
// as plain text (not a link), styled with `text-primary font-semibold`,
// and tagged `aria-current="page"`. The wrapping `<nav>` carries the
// `aria-label` so screen readers identify it as the breadcrumb landmark.
//
// EWM-194 — H1 + SKU moved into the info panel (right column) where Dan's
// mock places them. This component is breadcrumb-only.
const Header = ({ className, product }: PDPHeaderProps) => {
  const translate = useTranslations();
  const { locale } = useParams();
  const localeKey = locale as Locale;

  // EWM-539 — render the single CT category hierarchy ending at the
  // product's primaryWebCategory (top parent → subcats → product), not
  // every assigned category flat. When no primary category is set (or it
  // isn't among the assigned categories), show the product name only —
  // no category chain. Labels use clean CT names; hrefs derive from the
  // leaf slug path via the shared `buildCategoryCrumbs` util.
  const primary = product.primaryCategoryId
    ? (product.categories ?? []).find((category) => category.categoryId === product.primaryCategoryId)
    : undefined;

  const crumbs = primary
    ? buildCategoryCrumbs({
        name: primary.name,
        path: primary.paths?.[localeKey] ?? primary.path,
        ancestors: primary.ancestors,
      })
    : [];

  return (
    <nav aria-label={translate('common.breadcrumb')} className={className}>
      <ol className="text-13 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600">
        {crumbs.map((crumb, index) => (
          // Suffix with index so duplicate hrefs (possible if a category's
          // slug path is malformed/shorter than its ancestor chain) can't
          // collide as React keys.
          <li key={`${crumb.href}-${index}`} className="flex items-center gap-2">
            <Link href={crumb.href} className="hover:text-primary" underlineOnHover={false}>
              {crumb.label}
            </Link>
            <span className="text-gray-400" aria-hidden>
              /
            </span>
          </li>
        ))}
        <li>
          <span aria-current="page" className="text-primary font-semibold">
            {product.name}
          </span>
        </li>
      </ol>
    </nav>
  );
};

export default Header;
